<template>
  <div class="animated fadeIn">
    <div class="kt-portlet kt-portlet--mobile" style="position:relative">
      <div class="kt-portlet__body kt-portlet__body--fit" ref="cnt">
        <div class="m-4">
          <div class="filters-container d-flex flex-row justify-content-between">
            <TableFilters v-model="filterOptions" style="margin-right: 45px" @click="refresh" />
            <div>
              <div class="d-flex flex-row">
                <button
                  class="btn refresh btn-sm mr-1"
                  @click.prevent="cashSupply"
                >{{$t('Cash supply')}}</button>
                <button
                  class="btn refresh btn-sm"
                  style="margin-right: 45px;"
                  @click.prevent="cashWithdrawal"
                >{{$t('Cash deposit')}}</button>
              </div>
              <DropdownColumns v-model="fields" class="customize" />
            </div>
          </div>
          <div style="overflow:auto">
            <b-table
              id="balance-table"
              :busy.sync="isBusy"
              show-empty
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              :hover="true"
              :striped="true"
              selectable
              select-mode="single"
              :items="provider"
              :fields="fields"
              :current-page="currentPage"
              :per-page="perPage"
              @row-selected="rowSelected"
              tbody-tr-class="tr-pointer"
              v-columns-resizable="{afterResize: columnsWidthChanged}"
            >
              <template v-slot:empty="scope">
                <div class="text-center">{{ $t(scope.emptyText) }}</div>
              </template>

              <template v-slot:head()="scope">{{ $t(scope.label) }}</template>

              <template v-slot:cell(type)="data">
                <span v-if="data.value == 1">{{ $t("Supply") }}</span>
                <span v-if="data.value == 2">{{ $t("Deposit") }}</span>
                <span v-if="data.value == 3">{{ $t("Supply Cancelled") }}</span>
                <span v-if="data.value == 4">{{ $t("Deposit Cancelled") }}</span>
              </template>
              <template v-slot:cell(shop_id)="data">
                <span v-if="data.value.to">
                  <router-link :to="`/shops/${data.value.to}`">{{ data.value.name }}</router-link>
                </span>
                <span v-else>{{ data.value.name }}</span>
              </template>
              <template v-slot:cell(username)="data">
                <span v-if="data.value.to">
                  <router-link :to="`/users/${data.value.to}`">{{ data.value.name }}</router-link>
                </span>
                <span v-else>{{ data.value.name }}</span>
              </template>
              <template v-slot:cell(cash_operator_id)="data">
                <span v-if="data.value.to">
                  <router-link :to="`/cash-collectors/${data.value.to}`">{{ data.value.name }}</router-link>
                </span>
                <span v-else>{{ data.value.name }}</span>
              </template>
              <template v-slot:cell(cash_operator_id)="data">
                <span v-if="data.value.to">
                  <router-link :to="`/cash-collectors/${data.value.to}`">{{ data.value.name }}</router-link>
                </span>
                <span v-else>{{ data.value.name }}</span>
              </template>
              <template v-slot:cell(bank_details_id)="data">
                <span v-if="data.value.to">
                  <router-link :to="`/banks/${data.value.to}`">{{ data.value.name }}</router-link>
                </span>
                <span v-else>{{ data.value.name }}</span>
              </template>
              <template v-slot:cell(subtype_id)="data">
                <span v-if="data.value.to">
                  <router-link :to="`/subtypes/${data.value.to}`">{{ data.value.name }}</router-link>
                </span>
                <span v-else>{{ data.value.name }}</span>
              </template>
              <template v-slot:cell(amount)="data">
                <span>{{ roundThousands(data.value) }}</span>
              </template>
              <template v-slot:cell(timestamp)="data">
                <span>{{ mom_unix(data.value).format(tableDateShort) }}</span>
              </template>
              <template v-slot:cell(tx_timestamp)="data">
                <span>{{ mom_unix(data.value).format(tableDateMedium) }}</span>
              </template>
              <template v-slot:cell(canceled)="data">
                <span v-if="data.value">{{ $t("YES") }}</span>
              </template>
              <template v-slot:cell(action)="row">
                <b-button
                  v-if="!row.item.canceled && row.item.type !== 3 && row.item.type !== 4"
                  size="sm"
                  @click="cancelTransaction(row.item)"
                  class="mr-1"
                >{{$t("Cancel")}}</b-button>
              </template>
            </b-table>
          </div>
          <div class="container-fluid">
            <div class="row">
              <div style="white-space:nowrap;" class="my-2">
                <PaginationSelect
                  v-model="perPage"
                  :options="paginationOpts"
                  variant="brand"
                  size="sm"
                />
                <span>
                  &nbsp;
                  <i18n path="Showing {start}-{end} of {total} rows" tag="span">
                    <span slot="start">{{(currentPage-1) * perPage + 1}}</span>
                    <span slot="end">{{currentPage * perPage}}</span>
                    <span slot="total">{{totalRows}}</span>
                  </i18n>
                </span>
                <b-button
                  size="sm"
                  @click.stop="$root.$emit('bv::refresh::table', 'balance-table')"
                  class="ml-2 refresh"
                  variant="light"
                  :title="$t('Refresh')"
                >
                  <i class="icon-refresh"></i>
                </b-button>
              </div>
              <nav class="ml-auto mt-2">
                <b-pagination
                  size="md"
                  :total-rows="totalRows"
                  :per-page="perPage"
                  v-model="currentPage"
                />
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
    <b-modal id="bv-modal-cash-deposit" size="custom" scrollable>
      <template slot="modal-title">{{$t('Cash supply')}}</template>
      <template slot="modal-header-close">
        <span />
      </template>
      <CashTransactionDeposit :eshop_id="eshop_id" :shop_name="shop_name" />
      <template slot="modal-footer" slot-scope="{ cancel }">
        <b-button variant="secondary" @click="cancel()">{{$t('Cancel')}}</b-button>
      </template>
    </b-modal>
    <b-modal id="bv-modal-cash-withdrawal" size="custom" scrollable>
      <template slot="modal-title">{{$t('Cash deposit')}}</template>
      <template slot="modal-header-close">
        <span />
      </template>
      <CashTransactionWithdrawal :eshop_id="eshop_id" :shop_name="shop_name" />
      <template slot="modal-footer" slot-scope="{ cancel }">
        <b-button variant="secondary" @click="cancel()">{{$t('Cancel')}}</b-button>
      </template>
    </b-modal>
    <b-modal id="bv-modal-cash-cancel" size="md" scrollable>
      <template slot="modal-title">{{$t('Cash Transaction Cancel')}}</template>
      <template slot="modal-header-close">
        <span />
      </template>
      <CashTransactionCancel :eid="ecancel_id" />
      <template slot="modal-footer" slot-scope="{ cancel }">
        <b-button variant="secondary" @click="cancel()">{{$t('Cancel')}}</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import moment from "moment";
import startCase from "@/shared/strings";
import { errorConf, successConf } from "@/shared/notifications";
import PaginationSelect from "@/components/PaginationSelect";
import tableMixin from "@/shared/mixins/table.mixin";
import DropdownColumns from "@/components/DropdownColumns";
import FloatingLabel from "@/components/FloatingLabel";
import Multiselect from "@/components/multiselect-filters";
import TableFilters from "@/components/TableFilters";
import DateRangePicker from "@/components/calendar-range/DateRangePicker";
import timeFormat from "@/shared/timeFormat";
import CashTransactionDeposit from "./CashTransactionDeposit";
import CashTransactionWithdrawal from "./CashTransactionWithdrawal";
import CashTransactionCancel from "./CashTransactionCancel";

export default {
  components: {
    PaginationSelect,
    DropdownColumns,
    FloatingLabel,
    Multiselect,
    DateRangePicker,
    TableFilters,
    CashTransactionDeposit,
    CashTransactionWithdrawal,
    CashTransactionCancel
  },
  name: "cashTransactions",
  mixins: [tableMixin],
  data: function() {
    return {
      tableName: "cashTransactions",
      isBusy: false,

      filterOptions: [
        {
          field: "company_id",
          multiple: true,
          placeholder: "Company",
          trackBy: "eid",
          label: "name",
          translate: false,
          store: "companies",
          hide_select: true,
          filterOptions: ["tenant_id"],
          options: [],
          selected: [],
          postIgnore: true
        },
        {
          field: "tenant_id",
          multiple: true,
          placeholder: "Tenant",
          trackBy: "eid",
          label: "brand_name",
          translate: false,
          store: "tenants",
          hide_select: true,
          filterOptions: ["shopgroup_id", "shop_id"],
          options: [],
          originalOptions: [],
          selected: [],
          postIgnore: true,
          filterOptionsBy: (data, field, fieldValue) => {
            const selected = fieldValue.map(elem => elem.eid);
            const orig = data.list[1].originalOptions;
            if (
              selected.length == 0 &&
              data.list[1].options.length != orig.length
            ) {
              data.list[1].options = orig;
            } else if (selected.length != 0) {
              data.list[1].options = orig.filter(item => {
                if (selected.indexOf(item.ecompany_id) > -1) {
                  return true;
                }
                return false;
              });
            }
          }
        },

        {
          field: "shopgroup_id",
          multiple: true,
          placeholder: "Shop Group",
          trackBy: "eid",
          label: "name",
          translate: false,
          store: "shopGroups",
          hide_select: true,
          options: [],
          originalOptions: [],
          selected: [],
          filterOptions: ["shop_id"],
          postIgnore: true,
          filterOptionsBy: (data, field, fieldValue) => {
            const selected = fieldValue.map(elem => elem.eid);
            const index = 2;
            const orig = data.list[index].originalOptions;
            if (selected.length == 0) {
              data.list[index].options = orig;
            } else {
              data.list[index].options = orig.filter(item => {
                if (selected.indexOf(item.etenant_id) > -1) {
                  return true;
                }
                return false;
              });
            }
          }
        },
        {
          field: "shop_id",
          multiple: true,
          placeholder: "Shop",
          trackBy: "eid",
          label: "name",
          translate: false,
          store: "shops",
          hide_select: true,
          options: [],
          originalOptions: [],
          selected: [],
          filterOptionsBy: (data, field, fieldValue) => {
            let selectedTenants = [];
            let selectedShopGroups = [];

            if (field == "tenant_id") {
              selectedTenants = fieldValue.map(elem => elem.eid);
            } else {
              selectedTenants = data.list[0].selected.map(elem => elem.eid);
            }
            if (field == "shopgroup_id") {
              selectedShopGroups = fieldValue.map(elem => elem.eshop_ids);
              if (selectedShopGroups.length) {
                selectedShopGroups = [].concat.apply([], selectedShopGroups);
              }
            } else {
              selectedShopGroups = data.list[2].selected.map(
                elem => elem.eshop_ids
              );
              if (selectedShopGroups.length) {
                selectedShopGroups = [].concat.apply([], selectedShopGroups);
              }
            }
            const index = 3;
            const orig = data.list[index].originalOptions;

            //console.log("selectedTenants", selectedTenants);
            //console.log("selectedShopGroups", selectedShopGroups);

            if (!selectedTenants.length && !selectedShopGroups.length) {
              data.list[index].options = orig;
              //console.log("orig", orig);
            } else {
              let tmp = [];

              if (selectedTenants.length) {
                // filter by selected tenants
                tmp = orig.filter(item => {
                  if (selectedTenants.indexOf(item.etenant_id) > -1) {
                    return true;
                  }
                  return false;
                });
              }

              if (selectedShopGroups.length) {
                if (!selectedTenants.length && !selectedTenants.length) {
                  tmp = orig;
                }
                tmp = tmp.filter(item => {
                  if (selectedShopGroups.indexOf(item.eid) > -1) {
                    return true;
                  }
                  return false;
                });
              }

              //console.log("tmp", tmp);

              data.list[index].options = tmp;
            }
          }
        },
        {
          field: "timestamp",
          type: "date",
          opens: "right",
          timePicker: false,
          locale: {
            format: timeFormat.datePickerShort, //format of the dates displayed
            firstDay: 1, //ISO first day of week - see moment documentations for details
            showWeekNumbers: true //show week numbers on each row of the calendar
          },
          selected: {
            startDate: moment().startOf("month"),
            endDate: moment().endOf("month")
          },
          default: {
            startDate: moment().startOf("month"),
            endDate: moment().endOf("month")
          },
          ranges: false
        }
      ],

      sortBy: "timestamp",
      sortDesc: true,
      fields: [
        {
          key: "shop_id",
          label: "Shop",
          sortable: false,
          ignore: true,
          class: ""
        },
        {
          key: "username",
          label: "Username",
          sortable: false,
          ignore: true,
          class: ""
        },
        {
          key: "timestamp",
          label: "Date",
          sortable: true,
          ignore: true,
          class: ""
        },
        {
          key: "tx_timestamp",
          label: "Transaction Date",
          sortable: true,
          ignore: true,
          class: ""
        },
        {
          key: "type",
          label: "Type",
          sortable: false,
          ignore: true,
          class: ""
        },
        {
          key: "amount",
          label: "Amount",
          sortable: false,
          ignore: true,
          class: ""
        },
        {
          key: "cash_operator_id",
          label: "Cash Collector",
          sortable: false,
          ignore: true,
          class: ""
        },
        {
          key: "cash_order_number",
          label: "Supply/Deposit order number",
          sortable: false,
          ignore: true,
          class: ""
        },
        {
          key: "bank_details_id",
          label: "Bank",
          sortable: false,
          ignore: true,
          class: ""
        },
        {
          key: "bank_order_number",
          label: "Bank order number",
          sortable: false,
          ignore: true,
          class: ""
        },
        {
          key: "subtype_id",
          label: "Subtype",
          sortable: false,
          ignore: true,
          class: ""
        },
        {
          key: "observations",
          label: "Observations",
          sortable: false,
          ignore: true,
          class: ""
        },
        {
          key: "canceled",
          label: "Canceled",
          sortable: false,
          ignore: true,
          class: ""
        },
        {
          key: "action",
          label: "Action",
          sortable: false,
          ignore: true
        }
      ],
      currentPage: 1,
      perPage: 10,
      totalRows: 0,

      filter: null,
      filterField: null,

      selectedRow: null,
      eshop_id: null,
      shop_name: null,
      ecancel_id: null
    };
  },
  created() {
    this.$root.$on("bv::modal::hide", (bvEvent, modalId) => {
      if (
        modalId == "bv-modal-cash-deposit" ||
        modalId == "bv-modal-cash-withdrawal" ||
        modalId == "bv-modal-cash-cancel"
      ) {
        this.refresh();
        this.ecancel_id = null;
      }
    });
  },
  destroyed() {
    this.$root.$off("bv::modal::hide");
  },
  methods: {
    cancelTransaction(item) {
      console.log("cancel", item);
      this.ecancel_id = item.eid;
      this.$bvModal.show("bv-modal-cash-cancel");
    },
    provider(ctx, callback) {
      const loading = this.$loading.open({
        container: this.$refs.b
      });

      const paginationData = {
        rowsPerPage: this.perPage,
        page: this.currentPage,
        exactFields: [],
        exactValues: []
      };

      if (this.filter && this.filterField) {
        paginationData.likeValues = [this.filter];
        paginationData.likeFields = [this.filterField];
      } else {
        paginationData.likeValues = [];
        paginationData.likeFields = [];
      }

      if (this.sortBy) {
        paginationData.sortBy = [this.sortBy];
        paginationData.sortDirection = [this.sortDesc ? "DESC" : "ASC"];
      }

      const exactFields = [];
      const exactValues = [];

      this.filterOptions.forEach(item => {
        if (item.type != "date" && item.selected.length && !item.postIgnore) {
          exactFields.push(item.field);
          if (item.trackBy) {
            const tmp = [];
            item.selected.forEach(el => {
              tmp.push(el[item.trackBy]);
            });
            exactValues.push(tmp);
          } else {
            exactValues.push(item.selected);
          }
        } else if (item.type == "date") {
          if (item.selected.startDate && item.selected.endDate) {
            paginationData.dateField = item.field;
            paginationData.dateStart = item.selected.startDate;
            paginationData.dateEnd = item.selected.endDate;
          } else {
            paginationData.dateField = item.field;
            paginationData.dateStart = moment().startOf("month");
            paginationData.dateEnd = moment().endOf("month");
          }
        }
      });

      try {
        paginationData.dateStart = this.processDate(
          paginationData.dateStart,
          true
        );
        paginationData.dateEnd = this.processDate(paginationData.dateEnd);
      } catch (err) {
        console.log(err);
      }

      if (exactFields.length) {
        paginationData.exactFields = exactFields;
        paginationData.exactValues = exactValues;
      }

      const shopByID = this.$store.getters["cache/shopByID"];
      const bankByID = this.$store.getters["cache/bankByID"];
      const operatorByID = this.$store.getters["cache/operatorByID"];
      const subtypeByID = this.$store.getters["cache/subtypeByID"];

      Promise.all([
        this.$store.dispatch("cache/fetch", "shops").catch(e => e),
        this.$store.dispatch("cache/fetch", "banks").catch(e => e),
        this.$store.dispatch("cache/fetch", "operators").catch(e => e),
        this.$store.dispatch("cache/fetch", "subtypes").catch(e => e)
      ])
        .catch(err => {})
        .finally(() => {
          this.$store
            .dispatch("shopCashTX/fetch", paginationData)
            .then(resp => {
              this.totalRows = resp.data.total;
              resp.data.rows.forEach((item, index) => {
                if (item.ecash_operator_id) {
                  const data = operatorByID(item.ecash_operator_id);
                  if (data) {
                    item.cash_operator_id = {
                      to: item.ecash_operator_id,
                      name: data.full_name
                    };
                  } else {
                    item.cash_operator_id = {
                      to: null,
                      name: item.ecash_operator_id
                    };
                  }
                }
                if (item.eshop_id) {
                  const data = shopByID(item.eshop_id);
                  if (data) {
                    item.shop_id = {
                      to: item.eshop_id,
                      name: data.name
                    };
                  } else {
                    item.shop_id = {
                      to: null,
                      name: item.eshop_id
                    };
                  }
                }
                if (item.euser_id && item.username) {
                  item.username = {
                    to: item.euser_id,
                    name: item.username
                  };
                } else {
                  item.username = {
                    to: null,
                    name: item.username
                  };
                }

                if (item.ebank_details_id) {
                  const data = bankByID(item.ebank_details_id);
                  if (data) {
                    item.bank_details_id = {
                      to: item.ebank_details_id,
                      name: data.name
                    };
                  } else {
                    item.bank_details_id = {
                      to: null,
                      name: item.ebank_details_id
                    };
                  }
                }
                if (item.esubtype_id) {
                  const data = subtypeByID(item.esubtype_id);
                  if (data) {
                    item.subtype_id = {
                      to: item.esubtype_id,
                      name: data.name
                    };
                  } else {
                    item.subtype_id = {
                      to: null,
                      name: item.esubtype_id
                    };
                  }
                }
              });
              callback(resp.data.rows);
            })
            .catch(err => {
              this.$bvToast.toast(
                this.$t(err.message),
                errorConf(this.$t("Error"))
              );
              this.totalRows = 0;
              callback([]);
            })
            .finally(() => {
              loading.close();
            });
        });
      // Must return null or undefined to signal b-table that callback is being used
      return null;
    },
    processDate(dt, start) {
      let fdt = null;
      if (typeof dt.format !== "undefined") {
        fdt = dt.format().split("T")[0];
      } else {
        fdt = dt.toISOString().split("T")[0];
      }
      if (start) {
        return `${fdt}T00:00:00.000Z`;
      }
      return `${fdt}T23:59:59.999Z`;
    },
    redirect() {},
    refresh() {
      this.$root.$emit("bv::refresh::table", "balance-table");
    },
    rowSelected(rows) {
      const len = rows.length;
      if (len == 1) {
        this.selectedRow = rows[0];
        this.eshop_id = rows[0].eshop_id;
        const shopByID = this.$store.getters["cache/shopByID"];
        this.shop_name = shopByID(rows[0].eshop_id).name;
      } else {
        this.selectedRow = null;
        this.eshop_id = null;
        this.shop_name = null;
      }
    },
    showErrorNotification(err) {
      if (err.details && err.details.length) {
        this.$bvToast.toast(
          this.$t(err.details[0]),
          errorConf(this.$t("Error"))
        );
      } else {
        this.$bvToast.toast(this.$t(err.message), errorConf(this.$t("Error")));
      }
    },
    cashSupply() {
      this.$bvModal.show("bv-modal-cash-deposit");
    },
    cashWithdrawal() {
      this.$bvModal.show("bv-modal-cash-withdrawal");
    },
    cancel() {
      this.$bvModal.hide("bv-modal-cash-deposit");
      this.$bvModal.hide("bv-modal-cash-withdrawal");
      this.$bvModal.hide("bv-modal-cash-cancel");
    },
    showSuccessNotification(msg) {
      this.$bvToast.toast(this.$t(msg), successConf(this.$t("Success")));
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep #balance-table th {
  text-transform: capitalize;
}
</style>




